import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Sparkles,
  User,
  MessageCircle,
  Info,
  Pencil,
  Check,
  X,
  Search,
  ChevronUp,
  ChevronDown,
  CornerUpLeft,
  Download,
  ThumbsUp,
  ThumbsDown,
  ClipboardList,
  Copy,
  MoreHorizontal,
  BookmarkPlus,
  AlertTriangle,
  CheckCheck,
  Clock,
  RefreshCw,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { collection, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { api, dispatchCodeHint, dispatchFailed, type DispatchSummary } from '../lib/api';
import { ApiError, errorMessage } from '../lib/api-error';
import { t, useLocale } from '../lib/i18n';
import { MOD_KEY, isModKey } from '../lib/platform';
import { db } from '../lib/firebase';
import type { Channel, Message } from '../lib/types';
import { AiWhyPopover, type AiDecision, type Anchor } from './AiWhyPopover';
import { SaveToCannedModal } from './SaveToCannedModal';

const BUBBLE: Record<Message['type'], string> = {
  customer: 'bg-white text-gray-900 border border-gray-200',
  agent: 'bg-primary-600 text-white',
  ai: 'bg-violet-50 text-violet-900 border border-violet-200',
  system: 'bg-amber-50 text-amber-900 border border-amber-200',
};

const ALIGN: Record<Message['type'], string> = {
  customer: 'justify-start',
  agent: 'justify-end',
  ai: 'justify-end',
  system: 'justify-center',
};

const ICON: Record<Message['type'], React.ReactNode> = {
  customer: <User className="w-3 h-3" />,
  agent: <MessageCircle className="w-3 h-3" />,
  ai: <Sparkles className="w-3 h-3" />,
  system: <Info className="w-3 h-3" />,
};

const LABEL: Record<Message['type'], string> = {
  customer: 'Customer',
  agent: 'Agent',
  ai: 'AI',
  system: 'System',
};

const IMAGE_EXT = /\.(png|jpe?g|gif|webp)(\?.*)?$/i;

// ── Support-memory guard (server: RESOLUTION_GUARD_CODE) ──────────────
// Two outcomes, and the UI must not render them the same way:
//   verification_ask        -> HTTP 422, nothing saved. The agent has to be told
//                              what to do instead (teach it as a RULE), or they
//                              re-click Save forever — which is exactly what
//                              happened on chat rDvuFXrVO6aGKgbas9wj.
//   self_service_deflection -> HTTP 200 WITH a `warning`. The answer IS saved;
//                              this is a caution, never a failure.
type GuardReason = 'verification_ask' | 'self_service_deflection';

/** Pull the guard reason out of a rejected save.
 *
 *  The 422 body is `{ error: 'resolution_not_teachable', reason: <which>, … }`,
 *  and `ApiError` splits it: `.message` is the short copy (here just the `error`
 *  code) while the FULL raw body is kept on `.detail`. So `reason` only survives
 *  on `.detail` and both have to be scanned — reading `.message` alone matched
 *  the code, missed the reason, and silently fell through to the strict default
 *  on every guard hit.
 *
 *  Matching on the codes rather than JSON.parse is still deliberate: it keeps
 *  working whether the body is our guard envelope or a proxy's re-wrapping of
 *  it, and `.detail` is truncated, so it may not be parseable JSON at all. */
function guardReasonFromError(e: unknown): GuardReason | null {
  const msg =
    e instanceof ApiError
      ? `${e.message} ${e.detail ?? ''}`
      : e instanceof Error
        ? e.message
        : String(e ?? '');
  if (!msg.includes('resolution_not_teachable')) return null;
  if (msg.includes('verification_ask')) return 'verification_ask';
  if (msg.includes('self_service_deflection')) return 'self_service_deflection';
  return 'verification_ask'; // unknown reason on a hard block: show the strict copy
}

/** The 200-with-a-caution shape. `api.saveToMemory` is typed without it (the
 *  field is additive on the server), so it is narrowed here at the call site. */
type SaveMemoryResult = { ok?: boolean; error?: string; warning?: { reason?: string } };

/** The pinned explanation for a guard hit.
 *
 *  Extracted so it can render in BOTH places a guard hit can happen. It used to
 *  live inline inside the AI-turn correction popover, which meant a block on an
 *  AGENT'S OWN reply (the "Save to AI memory" item is offered on those too) set
 *  the notice and then rendered it nowhere — the operator got a toast that
 *  disappeared and no pinned reason. That is the same "explained once, briefly,
 *  then gone" failure this whole change exists to remove.
 *
 *  `onWriteRule` is optional ON PURPOSE: the per-message rule path only exists
 *  for an AI turn. Where there is no rule editor to switch to, the button is
 *  omitted and the copy names where rules live instead — a button that does
 *  nothing is worse than no button. */
function GuardNoticePanel({
  reason,
  onWriteRule,
  onDismiss,
}: {
  reason: GuardReason;
  onWriteRule?: () => void;
  onDismiss: () => void;
}) {
  const blocked = reason === 'verification_ask';
  return (
    <div
      role="status"
      dir="auto"
      className={`mb-1.5 p-2 rounded border text-[11px] leading-snug ${
        blocked ? 'bg-white border-rose-300 text-rose-800' : 'bg-amber-50 border-amber-300 text-amber-900'
      }`}
    >
      <div className="flex items-start gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
        <div className="min-w-0">
          <div className="font-semibold">
            {blocked ? t('thread.guard.block.title') : t('thread.guard.warn.title')}
          </div>
          <p className="mt-0.5">{blocked ? t('thread.guard.block.body') : t('thread.guard.warn.body')}</p>
          {blocked && (
            <>
              <p className="mt-1 font-medium">{t('thread.guard.block.remedy')}</p>
              {onWriteRule ? (
                <button
                  type="button"
                  onClick={onWriteRule}
                  className="mt-1 px-2 py-0.5 rounded border border-rose-400 bg-white text-rose-700 hover:bg-rose-50 font-medium"
                >
                  {t('thread.guard.block.switchToRule')}
                </button>
              ) : (
                <p className="mt-1">{t('thread.guard.block.remedyWhere')}</p>
              )}
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="ms-auto shrink-0 opacity-60 hover:opacity-100"
          aria-label={t('thread.guard.dismiss')}
          title={t('thread.guard.dismiss')}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// Renders the post-resolution CSAT survey system message without its internal
// `[csat:token]` marker (the customer sees a star widget; agents see this).
function CsatSurveyNote({ prompt }: { prompt: string }) {
  return (
    <div className="text-sm leading-snug" dir="auto">
      <span className="inline-flex items-center gap-1 font-medium">
        <ClipboardList className="w-3.5 h-3.5" /> Satisfaction survey sent
      </span>
      {prompt && <div className="text-[12px] opacity-80 mt-0.5">“{prompt}”</div>}
    </div>
  );
}

function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ── Delivery receipt ────────────────────────────────────────────────────────
// The honest answer to "did the customer actually get this?", read straight off
// the message doc. The rules it renders by:
//   undefined            → nothing. Docs written before delivery tracking know
//                          nothing, and a tick we cannot justify is precisely
//                          the lie this panel exists to stop telling.
//   pending              → "Sending…" — NO provider has acknowledged it yet.
//   sent/delivered/read  → one/two ticks, deliberately quiet.
//   failed               → loud: the provider's reason, its code, and a resend.
// A failed message is never allowed to look like a sent one — the bubble also
// takes a rose ring (see `failed` below), because the receipt line alone is
// exactly the kind of thing an eye skips over.

/** A 'pending' older than this is no longer "sending", it is stuck. */
const PENDING_STALE_MS = 2 * 60_000;

/** Channels with an external leg, mirroring the backend's DISPATCHED_CHANNELS.
 *  On web/api the widget's own listener IS the delivery, so a tick there says
 *  nothing — and a tick under every bubble is exactly how the ones that matter
 *  stop being read. Failures still show on every channel. */
const DISPATCHED_CHANNELS: ReadonlySet<string> = new Set(['whatsapp', 'email', 'sms', 'facebook']);

const DELIVERED_LABEL = {
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
} as const;

function DeliveryReceipt({
  m,
  channel,
  onResend,
  resending,
}: {
  m: Message;
  channel?: Channel;
  onResend?: () => void;
  resending: boolean;
}) {
  const status = m.deliveryStatus;
  if (!status) return null;
  // Unknown channel is treated as dispatched: showing a receipt we might not
  // have needed beats hiding a failure we did.
  const dispatched = !channel || DISPATCHED_CHANNELS.has(channel);
  // The agent bubble is solid primary-600, so its quiet text has to be light.
  const quiet = m.type === 'agent' ? 'text-primary-100/90' : 'text-gray-500';

  if (status === 'failed') {
    const hint = dispatchCodeHint(m.deliveryCode);
    return (
      <div
        role="alert"
        dir="auto"
        className="mt-1.5 rounded border border-rose-300 bg-white px-2 py-1.5 text-[11px] leading-snug text-rose-800"
      >
        <div className="flex items-center gap-1 font-semibold">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          Not delivered — the customer never got this
        </div>
        {m.deliveryError && <p className="mt-0.5 break-words">{m.deliveryError}</p>}
        {hint && <p className="mt-0.5 font-medium">{hint}</p>}
        <div className="mt-1 flex items-center gap-2">
          {onResend && (
            <button
              type="button"
              onClick={onResend}
              disabled={resending}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-rose-400 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-50 font-medium"
              // No re-dispatch route exists: this posts the text again as a new
              // message. Says "Resend" because that is what it does.
              title="Send this text again"
            >
              <RefreshCw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} />
              {resending ? 'Resending…' : 'Resend'}
            </button>
          )}
          {m.deliveryCode && <span className="opacity-70 font-mono">code {m.deliveryCode}</span>}
        </div>
      </div>
    );
  }

  if (status === 'pending') {
    const stale = Date.now() - m.createdAt > PENDING_STALE_MS;
    // Amber-600 on the solid primary-600 agent bubble is unreadable, which for a
    // warning is the same as not showing it.
    const staleTone = m.type === 'agent' ? 'text-amber-200 font-semibold' : 'text-amber-700 font-medium';
    return (
      <div
        className={`mt-1 flex items-center gap-1 text-[10px] ${stale ? staleTone : quiet}`}
        title={
          stale
            ? 'Handed to the channel but nothing has acknowledged it yet.'
            : 'Waiting for the channel to acknowledge it.'
        }
      >
        <Clock className="w-3 h-3" />
        {stale ? 'Still not acknowledged' : 'Sending…'}
      </div>
    );
  }

  if (!dispatched) return null;

  return (
    <div
      className={`mt-1 flex items-center gap-1 text-[10px] ${quiet}`}
      title={
        status === 'sent'
          ? 'Accepted by the channel. Delivery to the handset is not confirmed.'
          : DELIVERED_LABEL[status]
      }
    >
      {status === 'sent' ? <Check className="w-3 h-3" /> : <CheckCheck className="w-3 h-3" />}
      {DELIVERED_LABEL[status]}
    </div>
  );
}

// Local calendar-day bucket (year+month+date) used to decide when to drop a
// date divider between consecutive messages in a long thread.
function dayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Friendly day label for the divider: Today / Yesterday, else a full weekday date.
function formatDayLabel(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

// Escape then apply a small set of inline markdown transforms.
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderMarkdown(input: string, searchRe: RegExp | null): string {
  let s = escapeHtml(input);
  // Code spans first to avoid further transforms inside them.
  s = s.replace(/`([^`\n]+)`/g, '<code class="px-1 py-0.5 bg-black/10 rounded text-[0.9em] font-mono">$1</code>');
  // Bold then italic.
  s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^_])_([^_\n]+)_(?!_)/g, '$1<em>$2</em>');
  // Auto-link http/https URLs.
  s = s.replace(
    /\b(https?:\/\/[^\s<]+[^\s<.,;!?])/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="underline">$1</a>',
  );
  // Highlight search matches last (must operate on already-escaped text).
  if (searchRe) {
    s = s.replace(searchRe, (m) => `<mark class="bg-yellow-200 text-black rounded px-0.5">${m}</mark>`);
  }
  return s;
}

interface MessageThreadProps {
  messages: Message[];
  tenantCode?: string;
  chatId?: string;
  agentUid?: string;
  /** The conversation's channel. Message docs don't carry it, and it decides
   *  whether a quiet delivery receipt says anything worth reading. */
  channel?: Channel;
  onQuote?: (m: Message) => void;
}

export interface MessageThreadHandle {
  /** Open the in-thread search bar and focus its input (e.g. from a toolbar button). */
  openSearch: () => void;
}

export const MessageThread = forwardRef<MessageThreadHandle, MessageThreadProps>(function MessageThread(
  { messages, tenantCode, chatId, agentUid, channel, onQuote },
  ref,
) {
  // Re-render on a language switch so the correction popover's labels/toasts
  // follow the agent's locale (t() reads the module-level current locale).
  useLocale();
  const endRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [busy, setBusy] = useState(false);
  /** Message whose text is being pushed out again after a failed delivery. */
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [matchIdx, setMatchIdx] = useState(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [customerTyping, setCustomerTyping] = useState(false);
  const lastCustTypingRef = useRef(0);
  const custTypingHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Per-turn AI decisions for this chat — powers the "why" popover on AI bubbles.
  const [decisions, setDecisions] = useState<AiDecision[]>([]);
  const [whyOpenId, setWhyOpenId] = useState<string | null>(null);
  const [whyAnchor, setWhyAnchor] = useState<Anchor | null>(null);

  useEffect(() => {
    if (!chatId) return;
    const q = query(collection(db, 'chats', chatId, 'ai_decisions'), orderBy('at', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => setDecisions(snap.docs.map((d) => ({ id: d.id, ...(d.data() as AiDecision) }))),
      () => setDecisions([])
    );
    return () => unsub();
  }, [chatId]);

  const decisionByMsg = useMemo(() => {
    const m = new Map<string, AiDecision>();
    for (const d of decisions) if (d.messageId) m.set(d.messageId, d);
    return m;
  }, [decisions]);

  // Map an AI message to its decision: exact by messageId, else the latest
  // decision recorded at/just-before the message timestamp (older turns that
  // predate messageId stamping). `decisions` is sorted ascending by `at`.
  function decisionForMessage(m: Message): AiDecision | undefined {
    const exact = decisionByMsg.get(m.id);
    if (exact) return exact;
    let best: AiDecision | undefined;
    for (const d of decisions) {
      if (typeof d.at === 'number' && d.at <= m.createdAt + 2000) best = d;
      else if (typeof d.at === 'number') break;
    }
    return best;
  }

  // ONE set of messages saved into the bot's support memory (AI replies AND the
  // agent's own replies). Replaces the old promote/teach split — see saveAiMemory.
  const [aiMemory, setAiMemory] = useState<Set<string>>(new Set());
  const [cannedContent, setCannedContent] = useState<string | null>(null);
  // "Bad answer" correction editor: which AI message is being corrected + draft.
  const [badEditId, setBadEditId] = useState<string | null>(null);
  const [badDraft, setBadDraft] = useState('');
  // Which KIND of correction is being written. 'answer' (default, the original
  // behaviour) teaches a replacement ANSWER into support memory; 'rule' teaches
  // a behavioural rule and must NOT touch support memory — a rule embedded there
  // ("stop asking for his card digits") comes back out as an authoritative
  // customer-facing answer. Separate draft state so switching modes doesn't
  // clobber the answer box, which is prefilled with the AI's own text.
  const [badMode, setBadMode] = useState<'answer' | 'rule'>('answer');
  const [ruleDraft, setRuleDraft] = useState('');
  // Per-message overflow ("···") menu — secondary actions live here so the bar
  // shows only 👍/👎 inline (discoverable on touch).
  const [moreOpenId, setMoreOpenId] = useState<string | null>(null);
  const [badBusy, setBadBusy] = useState(false);
  // A guard outcome that needs more than a toast: the explanation, and for the
  // hard block the way through. Rendered inline in the correction popover so it
  // survives longer than the toast and sits next to the control that fixes it.
  const [guardNotice, setGuardNotice] = useState<{ messageId: string; reason: GuardReason } | null>(null);
  // 'warn' is a third state on purpose: a self-service save SUCCEEDS, and
  // painting it rose (the old two-state toast) reads as the failure this whole
  // change exists to stop showing.
  const [toast, setToast] = useState<{ msg: string; kind: 'ok' | 'warn' | 'error' } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  function showToast(msg: string, ok: boolean | 'warn' = true) {
    const kind = ok === 'warn' ? 'warn' : ok ? 'ok' : 'error';
    setToast({ msg, kind });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    // A plain confirmation can flash by; an explanation cannot be read in 2.6s.
    toastTimer.current = setTimeout(() => setToast(null), kind === 'ok' ? 2600 : 7000);
  }

  async function copyMessage(m: Message) {
    try {
      await navigator.clipboard.writeText(m.content);
      showToast('Copied', true);
    } catch {
      showToast('Copy failed', false);
    }
  }

  /** What the reply endpoint's dispatch summary means, in one line. */
  function dispatchToast(d: DispatchSummary | undefined) {
    if (dispatchFailed(d)) {
      const hint = dispatchCodeHint(d?.code);
      showToast(`Still not delivered — ${d?.reason ?? 'the channel refused it'}${hint ? ` ${hint}` : ''}`, false);
      return;
    }
    if (d?.status === 'skipped') {
      // web/api: the widget's own listener is the delivery, nothing was sent.
      showToast('Posted to the conversation.', true);
      return;
    }
    showToast('Sent.', true);
  }

  // Resend a message the channel never delivered. There is NO re-dispatch route:
  // the backend can only create a new message, so this posts the same text again
  // and reports what the provider said this time. Deliberately manual — an
  // automatic retry against a token that expired 19 days ago would have retried
  // for 19 days and told nobody.
  async function resendMessage(m: Message) {
    if (!tenantCode || !chatId || !agentUid) return;
    setResendingId(m.id);
    try {
      const r = await api.reply({ tenantCode, chatId, agentUid, content: m.content, internal: false });
      dispatchToast(r.dispatch);
    } catch (e) {
      console.error('resend failed', e);
      showToast(errorMessage(e), false);
    } finally {
      setResendingId(null);
    }
  }

  // Save an answer into the bot's support memory (an approved resolution the bot
  // reuses). Works for AI replies AND the agent's own replies — a manual click is
  // always a deliberate approval, so there is no "unapproved" variant. Toggle:
  // re-click reverts via deleteFromMemory. Replaces the old promote/teach split.
  async function saveAiMemory(m: Message, resolution?: string) {
    if (!tenantCode || !chatId || !agentUid) return;
    if (aiMemory.has(m.id)) {
      setAiMemory((s) => { const n = new Set(s); n.delete(m.id); return n; });
      try {
        const r = await api.deleteFromMemory(tenantCode, agentUid, chatId, m.id);
        if ((r as { ok?: boolean }).ok === false) throw new Error('delete failed');
        showToast(t('thread.memory.removed'), true);
      } catch (e) {
        console.error('remove from memory failed', e);
        showToast(t('thread.memory.removeFailed'), false);
        setAiMemory((s) => new Set(s).add(m.id));
      }
      return;
    }
    setAiMemory((s) => new Set(s).add(m.id));
    try {
      const r = (await api.saveToMemory(tenantCode, agentUid, chatId, {
        resolution: resolution ?? m.content,
        approved: true,
        messageId: m.id,
      })) as SaveMemoryResult;
      if (r.ok === false) throw new Error(r.error || 'save failed');
      if (r.warning) {
        // Saved AND flagged. Caution, not failure — the bookmark stays set.
        setGuardNotice({ messageId: m.id, reason: 'self_service_deflection' });
        showToast(t('thread.guard.warn.toast'), 'warn');
      } else {
        showToast(t('thread.memory.saved'), true);
      }
    } catch (e) {
      console.error('save to memory failed', e);
      setAiMemory((s) => { const n = new Set(s); n.delete(m.id); return n; });
      const reason = guardReasonFromError(e);
      if (reason) {
        // Refused, and the agent needs to know why + what to do instead. For an
        // AI turn the remedy is one control away, so open the correction popover
        // on the RULE tab with the explanation pinned above it.
        setGuardNotice({ messageId: m.id, reason });
        if (m.type === 'ai') {
          setMoreOpenId(null);
          setBadDraft(m.content);
          setRuleDraft('');
          setBadMode('rule');
          setBadEditId(m.id);
        }
        showToast(t('thread.guard.block.toast'), false);
        return;
      }
      showToast(t('thread.memory.saveFailed'), false);
    }
  }

  async function rateMessage(m: Message, d: AiDecision | undefined, verdict: 'good' | 'bad', reason?: string) {
    if (!tenantCode || !chatId || !agentUid) return;
    // Optimistic: stamp feedback locally so the popover reflects it immediately.
    if (d?.id) {
      setDecisions((prev) =>
        prev.map((x) => (x.id === d.id ? { ...x, feedback: { verdict, reason, by: agentUid } } : x))
      );
    }
    try {
      await api.rateAiMessage(tenantCode, agentUid, chatId, {
        messageId: m.id,
        aiDecisionId: d?.aiDecisionId ?? d?.id,
        verdict,
        reason,
      });
      showToast(verdict === 'good' ? 'Thanks — marked as a good answer' : 'Thanks — flagged for review', true);
    } catch (e) {
      console.error('ai feedback failed', e);
      showToast('Could not save feedback', false);
    }
  }

  // "Bad" with a correction: record the bad verdict AND teach the corrected
  // answer back to the bot's support memory (same approved path as "Promote to
  // AI memory"), so the AI reuses the RIGHT answer next time — closing the
  // customer_support feedback loop with data, not a prompt change. If the agent
  // didn't change the text, it degrades to a plain downvote.
  async function submitBadCorrection(m: Message) {
    if (!tenantCode || !chatId || !agentUid) return;
    const corrected = badDraft.trim();
    const changed = corrected.length > 0 && corrected !== m.content.trim();
    if (!changed) {
      setBadEditId(null);
      await rateMessage(m, decisionForMessage(m), 'bad');
      return;
    }
    setBadBusy(true);
    try {
      // The feedback route caps `reason` at 280 chars; a longer correction used
      // to 400 and lose the reason entirely (the API helper swallows the error).
      await rateMessage(m, decisionForMessage(m), 'bad', corrected.slice(0, 280));
      const r = (await api.saveToMemory(tenantCode, agentUid, chatId, {
        resolution: corrected,
        approved: true,
        messageId: m.id,
      })) as SaveMemoryResult;
      if (r.ok === false) throw new Error(r.error || 'teach failed');
      setAiMemory((s) => new Set(s).add(m.id));
      if (r.warning) {
        // Saved with a caution. Close the editor exactly as on a clean save —
        // the note rides on the toast, and re-opening would look like a failure.
        setGuardNotice({ messageId: m.id, reason: 'self_service_deflection' });
        showToast(t('thread.guard.warn.toast'), 'warn');
      } else {
        showToast(t('thread.fix.answerSaved'), true);
      }
      setBadEditId(null);
    } catch (e) {
      console.error('bad correction failed', e);
      const reason = guardReasonFromError(e);
      if (reason) {
        // Keep the popover OPEN and switch it to the rule tab: the correction
        // the agent typed is still in the box, and the remedy is right there.
        setGuardNotice({ messageId: m.id, reason });
        setBadMode('rule');
        setBadEditId(m.id);
        showToast(t('thread.guard.block.toast'), false);
        return;
      }
      showToast(t('thread.fix.answerFailed'), false);
    } finally {
      setBadBusy(false);
    }
  }

  // "Bad" with a behavioural RULE: record the bad verdict and turn the rule into
  // a DRAFT support flow (ai_flows) triggered by the same intent/keywords as the
  // turn it was written about. Deliberately never calls saveToMemory — support
  // memory is replayed to customers as a verified answer, so a rule stored there
  // is a fabricated customer-facing reply. Nothing reaches a customer until the
  // draft is approved in Settings -> Support flows.
  async function submitBadRule(m: Message) {
    if (!tenantCode || !chatId || !agentUid) return;
    const rule = ruleDraft.trim();
    if (rule.length < 3) return;
    setBadBusy(true);
    try {
      await rateMessage(m, decisionForMessage(m), 'bad', rule.slice(0, 280));
      await api.saveFlowFromCorrection(tenantCode, agentUid, chatId, { messageId: m.id, rule });
      showToast(t('thread.fix.ruleSaved'), true);
      setBadEditId(null);
      setRuleDraft('');
    } catch (e) {
      console.error('teach rule failed', e);
      showToast(t('thread.fix.ruleFailed'), false);
    } finally {
      setBadBusy(false);
    }
  }

  // Subscribe to chat doc to know when the customer is actively typing. Driven
  // off timestamp *transitions* + a client-relative hide timer (skew-immune):
  // we never subtract the server-written timestamp from our own clock (clock
  // skew silently broke the old freshness window). Re-armed by each ~3s
  // heartbeat, hidden on the explicit 0 write or after the safety timeout.
  useEffect(() => {
    if (!chatId) return;
    const unsub = onSnapshot(doc(db, 'chats', chatId), (snap) => {
      const ts = (snap.data() as { customerTypingAt?: number } | undefined)?.customerTypingAt ?? 0;
      if (ts === 0) {
        if (custTypingHideRef.current) {
          clearTimeout(custTypingHideRef.current);
          custTypingHideRef.current = null;
        }
        lastCustTypingRef.current = 0;
        setCustomerTyping(false);
      } else if (ts !== lastCustTypingRef.current) {
        lastCustTypingRef.current = ts;
        setCustomerTyping(true);
        if (custTypingHideRef.current) clearTimeout(custTypingHideRef.current);
        custTypingHideRef.current = setTimeout(() => {
          setCustomerTyping(false);
          lastCustTypingRef.current = 0;
        }, 8000);
      }
    });
    return () => {
      unsub();
      if (custTypingHideRef.current) clearTimeout(custTypingHideRef.current);
    };
  }, [chatId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, customerTyping]);

  // Ctrl/Cmd-F opens in-thread search.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isModKey(e) && e.key.toLowerCase() === 'f') {
        const root = rootRef.current;
        if (!root) return;
        // Only intercept when the thread is in view / focused area.
        const active = document.activeElement;
        const inside = active && root.contains(active);
        if (inside || document.hasFocus()) {
          e.preventDefault();
          setSearchOpen(true);
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }
      } else if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [searchOpen]);

  // Let a parent (the chat toolbar's Search button) open the same in-thread
  // search the Ctrl/Cmd-F shortcut opens, so the feature is discoverable.
  useImperativeHandle(ref, () => ({
    openSearch: () => {
      setSearchOpen(true);
      setTimeout(() => searchInputRef.current?.focus(), 0);
    },
  }), []);

  const searchRe = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const escaped = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(escaped, 'gi');
  }, [searchQuery]);

  const matches = useMemo(() => {
    if (!searchRe) return [] as string[];
    return messages.filter((m) => searchRe.test(m.content)).map((m) => m.id);
  }, [messages, searchRe]);

  useEffect(() => {
    setMatchIdx(0);
    if (matches.length > 0) {
      const el = document.getElementById(`msg-${matches[0]}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [searchQuery, matches.length]);

  function gotoMatch(delta: number) {
    if (matches.length === 0) return;
    const next = (matchIdx + delta + matches.length) % matches.length;
    setMatchIdx(next);
    const el = document.getElementById(`msg-${matches[next]}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  async function saveEdit(messageId: string) {
    if (!tenantCode || !chatId || !agentUid) return;
    if (!editDraft.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await api.editMessage(tenantCode, agentUid, chatId, messageId, editDraft.trim());
      setEditingId(null);
      setEditDraft('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function toggleReaction(m: Message, emoji: string) {
    if (!tenantCode || !chatId || !agentUid) return;
    const current = m.reactions?.[emoji] ?? [];
    const op: 'add' | 'remove' = current.includes(agentUid) ? 'remove' : 'add';
    try {
      await api.reactToMessage(tenantCode, agentUid, chatId, m.id, emoji, op);
    } catch (e) {
      console.error('reaction failed', e);
    }
  }

  function scrollToMessage(id: string) {
    const el = document.getElementById(`msg-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-primary-400');
      setTimeout(() => el.classList.remove('ring-2', 'ring-primary-400'), 1200);
    }
  }

  return (
    <div ref={rootRef} tabIndex={-1} className="flex flex-col gap-3 p-4 relative" data-thread>
      {searchOpen && (
        <div className="sticky top-0 z-30 flex justify-end -mb-2">
          <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-md shadow-md px-2 py-1">
            <Search className="w-3.5 h-3.5 text-gray-500" />
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search in thread"
              // The shortcut label follows the keyboard the agent actually has;
              // the handler above takes either modifier regardless.
              title={`Find in thread (${MOD_KEY}+F)`}
              className="text-xs px-1 py-0.5 outline-none w-44"
            />
            <span className="text-[10px] text-gray-500 tabular-nums px-1">
              {matches.length > 0 ? `${matchIdx + 1}/${matches.length}` : '0/0'}
            </span>
            <button
              type="button"
              onClick={() => gotoMatch(-1)}
              className="text-gray-500 hover:text-gray-800 p-0.5"
              title="Previous"
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => gotoMatch(1)}
              className="text-gray-500 hover:text-gray-800 p-0.5"
              title="Next"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => {
                setSearchOpen(false);
                setSearchQuery('');
              }}
              className="text-gray-500 hover:text-gray-800 p-0.5"
              title="Close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
      {messages.map((m, i) => {
        const isEditable =
          m.type === 'agent' && !m.internal && agentUid && m.senderId === agentUid;
        const isEditing = editingId === m.id;
        const edited = (m as unknown as { editedAt?: number }).editedAt;
        const reactionEntries = m.reactions
          ? Object.entries(m.reactions).filter(([, uids]) => Array.isArray(uids) && uids.length > 0)
          : [];
        // Drop a date divider before the first message and whenever the calendar
        // day changes — so a long, multi-day thread stays anchored in time.
        const prev = i > 0 ? messages[i - 1] : null;
        const showDay = !prev || dayKey(prev.createdAt) !== dayKey(m.createdAt);
        // Delivery only means something for messages WE tried to push out: an
        // inbound customer message has no leg of ours, an internal note is never
        // sent anywhere.
        const outbound = m.type !== 'customer' && !m.internal;
        const failedSend = outbound && m.deliveryStatus === 'failed';
        return (
          <div key={m.id} className="contents">
          {showDay && (
            <div className="flex justify-center select-none">
              <span className="text-[11px] font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-3 py-0.5 shadow-sm">
                {formatDayLabel(m.createdAt)}
              </span>
            </div>
          )}
          <div id={`msg-${m.id}`} className={`flex ${ALIGN[m.type]} group transition-shadow`}>
            <div
              className={`${isEditing ? 'w-[32rem] max-w-[92%]' : 'max-w-[70%]'} ${
                m.internal
                  ? 'bg-amber-50 border border-dashed border-amber-400 text-amber-900'
                  : BUBBLE[m.type]
              } rounded-lg px-3 py-2 shadow-sm relative ${
                // A message that never reached the customer must not read as one
                // that did — the receipt line below is easy to skim past, the
                // ring is not.
                failedSend ? 'ring-2 ring-rose-500 ring-offset-1' : ''
              }`}
            >
              {/* Action bar. 👍/👎 are inline for AI replies (the high-frequency
                  rating); everything else lives behind a "···" overflow so the
                  bar stays light and the actions are reachable on touch. The bar
                  is faintly persistent (discoverable) and solid on hover/focus. */}
              {m.type !== 'system' && !isEditing && (() => {
                const d = m.type === 'ai' ? decisionForMessage(m) : undefined;
                const rated = d?.feedback?.verdict;
                const inMem = aiMemory.has(m.id);
                const canMem =
                  m.type === 'ai' || (m.type === 'agent' && !m.internal && !!agentUid && m.senderId === agentUid);
                const item = 'w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2 text-gray-700';
                return (
                  <div
                    className={`absolute -top-3 ${m.type === 'customer' ? 'right-2' : 'left-2'} z-20 opacity-75 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-0.5 bg-white border border-gray-200 rounded-full shadow px-1 py-0.5`}
                  >
                    {m.type === 'ai' && (
                      <>
                        <button
                          type="button"
                          onClick={() => rateMessage(m, d, 'good')}
                          className={`p-0.5 rounded-full hover:bg-gray-100 ${rated === 'good' ? 'text-emerald-600' : 'text-gray-500'}`}
                          title="Good answer"
                        >
                          <ThumbsUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMoreOpenId(null);
                            setBadDraft(m.content);
                            setRuleDraft('');
                            setBadMode('answer');
                            setGuardNotice(null); // a fresh correction starts clean
                            setBadEditId(badEditId === m.id ? null : m.id);
                          }}
                          className={`p-0.5 rounded-full hover:bg-gray-100 ${rated === 'bad' || badEditId === m.id ? 'text-rose-600' : 'text-gray-500'}`}
                          title="Bad answer — correct it & teach the AI"
                        >
                          <ThumbsDown className="w-3 h-3" />
                        </button>
                      </>
                    )}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setMoreOpenId(moreOpenId === m.id ? null : m.id)}
                        className={`p-0.5 rounded-full hover:bg-gray-100 ${moreOpenId === m.id ? 'text-gray-800' : 'text-gray-500'}`}
                        title="More actions"
                      >
                        <MoreHorizontal className="w-3 h-3" />
                      </button>
                      {moreOpenId === m.id && (
                        <div
                          className={`absolute top-full mt-1 ${m.type === 'customer' ? 'right-0' : 'left-0'} z-30 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[176px] text-xs`}
                          onMouseLeave={() => setMoreOpenId(null)}
                        >
                          {m.type === 'ai' && (
                            <button
                              type="button"
                              className={item}
                              onClick={(e) => {
                                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                setWhyAnchor({ top: r.top, left: r.left, right: r.right, bottom: r.bottom });
                                setWhyOpenId(whyOpenId === m.id ? null : m.id);
                                setMoreOpenId(null);
                              }}
                            >
                              <Info className="w-3.5 h-3.5 text-violet-600" /> Why this answer
                            </button>
                          )}
                          {canMem && (
                            <button type="button" className={item} onClick={() => { saveAiMemory(m); setMoreOpenId(null); }}>
                              {inMem ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Sparkles className="w-3.5 h-3.5 text-gray-500" />}
                              {inMem ? 'Saved to AI memory' : 'Save to AI memory'}
                            </button>
                          )}
                          {canMem && (
                            <button type="button" className={item} onClick={() => { setCannedContent(m.content); setMoreOpenId(null); }}>
                              <BookmarkPlus className="w-3.5 h-3.5 text-gray-500" /> Save to canned
                            </button>
                          )}
                          <button type="button" className={item} onClick={() => { copyMessage(m); setMoreOpenId(null); }}>
                            <Copy className="w-3.5 h-3.5 text-gray-500" /> Copy text
                          </button>
                          {onQuote && (
                            <button type="button" className={item} onClick={() => { onQuote(m); setMoreOpenId(null); }}>
                              <CornerUpLeft className="w-3.5 h-3.5 text-gray-500" /> Reply
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              {m.type === 'ai' && whyOpenId === m.id && (
                <AiWhyPopover
                  decision={decisionForMessage(m) ?? {}}
                  anchor={whyAnchor}
                  onClose={() => setWhyOpenId(null)}
                />
              )}
              {/* Guard hit on a message with no correction popover to pin it in —
                  an agent's OWN reply (canMem offers "Save to AI memory" there
                  too). Without this the notice was set and rendered nowhere, so
                  the only explanation was a toast that timed out. */}
              {guardNotice?.messageId === m.id && !(m.type === 'ai' && badEditId === m.id) && (
                <div className="mt-1 mb-1 max-w-[85%]">
                  <GuardNoticePanel
                    reason={guardNotice.reason}
                    onDismiss={() => setGuardNotice(null)}
                  />
                </div>
              )}
              {m.type === 'ai' && badEditId === m.id && (
                <div className="mt-1 mb-1 p-2 bg-rose-50 border border-rose-200 rounded-lg text-xs max-w-[85%]">
                  <div className="font-medium text-rose-700 mb-1">{t('thread.fix.title')}</div>
                  {/* Why the last save was refused / flagged. Without this the
                      server's 422 reached the agent as "could not save the
                      correction" and they simply pressed it again. */}
                  {guardNotice?.messageId === m.id && (
                    <GuardNoticePanel
                      reason={guardNotice.reason}
                      onWriteRule={() => setBadMode('rule')}
                      onDismiss={() => setGuardNotice(null)}
                    />
                  )}
                  {/* Two outcomes with two DIFFERENT destinations. "Right answer"
                      embeds a replacement answer into support memory, which the
                      bot may repeat to a customer verbatim; "what's wrong" is a
                      behavioural rule and goes to the draft-playbook path. Agents
                      typing rules into the answer box is exactly what poisoned
                      support memory with fake customer-facing answers. */}
                  <div className="flex items-center gap-1 mb-1.5">
                    {(['answer', 'rule'] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        aria-pressed={badMode === mode}
                        onClick={() => setBadMode(mode)}
                        className={`px-2 py-0.5 rounded-full border text-[11px] ${
                          badMode === mode
                            ? 'bg-rose-600 text-white border-rose-600'
                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {mode === 'answer' ? t('thread.fix.mode.answer') : t('thread.fix.mode.rule')}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={badMode === 'answer' ? badDraft : ruleDraft}
                    onChange={(e) => (badMode === 'answer' ? setBadDraft(e.target.value) : setRuleDraft(e.target.value))}
                    rows={4}
                    dir="auto"
                    // Matches the server's `rule` cap so a long rule is trimmed in
                    // the box instead of 400-ing on submit.
                    maxLength={badMode === 'rule' ? 600 : undefined}
                    placeholder={badMode === 'answer' ? t('thread.fix.answerPlaceholder') : t('thread.fix.rulePlaceholder')}
                    className="w-full border border-rose-200 rounded p-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-rose-300 resize-y"
                  />
                  {badMode === 'rule' && (
                    <p className="mt-1 text-[11px] text-rose-700/80" dir="auto">{t('thread.fix.ruleHint')}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      type="button"
                      disabled={badBusy || (badMode === 'rule' && ruleDraft.trim().length < 3)}
                      onClick={() => (badMode === 'answer' ? submitBadCorrection(m) : submitBadRule(m))}
                      className="px-2 py-1 bg-rose-600 text-white rounded font-medium hover:bg-rose-700 disabled:opacity-50"
                    >
                      {badMode === 'answer' ? t('thread.fix.saveAnswer') : t('thread.fix.saveRule')}
                    </button>
                    <button
                      type="button"
                      disabled={badBusy}
                      onClick={() => { setBadEditId(null); rateMessage(m, decisionForMessage(m), 'bad'); }}
                      className="px-2 py-1 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      title={t('thread.fix.flagOnlyHint')}
                    >
                      {t('thread.fix.flagOnly')}
                    </button>
                    <button
                      type="button"
                      disabled={badBusy}
                      onClick={() => setBadEditId(null)}
                      className="px-2 py-1 text-gray-500 hover:text-gray-700"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              )}
              {m.quotedMessageId && (
                <button
                  type="button"
                  onClick={() => scrollToMessage(m.quotedMessageId!)}
                  className="mb-1 w-full text-left px-2 py-1 bg-black/10 rounded text-[11px] flex items-center gap-1 opacity-90 hover:opacity-100"
                >
                  <CornerUpLeft className="w-3 h-3 shrink-0" />
                  <span className="truncate" dir="auto">
                    {m.quotedPreview ?? 'quoted message'}
                  </span>
                </button>
              )}
              <div
                className={`flex items-center gap-1 text-[11px] mb-1 ${
                  m.type === 'agent' ? 'text-primary-100' : 'text-gray-500'
                } ${m.internal ? 'text-amber-700 font-semibold' : ''}`}
              >
                {m.internal ? <Info className="w-3 h-3" /> : ICON[m.type]}
                {m.internal
                  ? 'INTERNAL NOTE'
                  : m.type === 'agent' && m.senderName
                    ? m.senderName
                    : LABEL[m.type]}
                <span className="opacity-70">·</span>
                <span className="opacity-70">{formatTime(m.createdAt)}</span>
                {edited && (
                  <span
                    className="opacity-70 italic cursor-help"
                    title={`Edited ${formatTime(edited)} — the customer sees the updated text`}
                  >
                    · edited
                  </span>
                )}
                {isEditable && !isEditing && (
                  <button
                    onClick={() => {
                      setEditingId(m.id);
                      setEditDraft(m.content);
                      setErr(null);
                    }}
                    className={`ml-auto opacity-0 group-hover:opacity-100 transition-opacity ${
                      m.type === 'agent' ? 'text-primary-100 hover:text-white' : 'text-gray-400 hover:text-gray-700'
                    }`}
                    title="Edit message"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </div>
              {isEditing ? (
                <div>
                  <textarea
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    rows={Math.min(8, Math.max(2, editDraft.split('\n').length))}
                    autoFocus
                    dir="auto"
                    className="w-full text-sm bg-white text-gray-900 border border-primary-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setEditingId(null);
                        setEditDraft('');
                      }
                      if (e.key === 'Enter' && isModKey(e)) saveEdit(m.id);
                    }}
                  />
                  {err && <div className="text-xs text-red-200 mt-1">{err}</div>}
                  <div className="mt-2 flex gap-2 justify-end">
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setEditDraft('');
                      }}
                      disabled={busy}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      <X className="w-3 h-3" /> Cancel
                    </button>
                    <button
                      onClick={() => saveEdit(m.id)}
                      disabled={busy || !editDraft.trim()}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      <Check className="w-3 h-3" /> {busy ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {(m as { subject?: string }).subject && (
                    <div className="mb-1.5 pb-1.5 border-b border-black/10 text-[11px] uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/></svg>
                      <span className="font-medium text-gray-700 truncate" dir="auto">
                        {(m as { subject?: string }).subject}
                      </span>
                    </div>
                  )}
                  {m.type === 'system' && /\[csat:[^\]]+\]/.test(m.content) ? (
                    <CsatSurveyNote prompt={m.content.replace(/\[csat:[^\]]+\]/, '').trim()} />
                  ) : (
                    <div
                      className="text-sm leading-snug whitespace-pre-wrap break-words"
                      dir="auto"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content, searchRe) }}
                    />
                  )}
                  {(() => {
                    type Att = { path?: string; url?: string; mimeType?: string; name?: string };
                    const newAtts: Att[] = ((m as unknown as { attachments?: Att[] }).attachments) ?? [];
                    const legacy: Att[] = m.attachmentPath ? [{ path: m.attachmentPath, url: m.attachmentPath }] : [];
                    const items = newAtts.length > 0 ? newAtts : legacy;
                    if (items.length === 0) return null;
                    return (
                      <div className="mt-2 space-y-2">
                        {items.map((att, i) => {
                          const href = att.url || att.path || '';
                          const mime = att.mimeType || '';
                          const name = att.name || (att.path || '').split('/').pop() || 'attachment';
                          const isImage = mime.startsWith('image/') || IMAGE_EXT.test(href);
                          const isAudio = mime.startsWith('audio/');
                          const isVideo = mime.startsWith('video/');
                          if (isImage) {
                            return (
                              <a key={i} href={href} target="_blank" rel="noopener noreferrer">
                                <img src={href} alt="attachment" className="max-h-60 rounded border border-black/10" />
                              </a>
                            );
                          }
                          if (isAudio) {
                            return (
                              <div key={i} className="space-y-1">
                                <audio src={href} controls className="max-w-full" preload="metadata" />
                                {m.transcript && m.transcript.trim().length > 0 && (
                                  <div className="text-[11px] italic opacity-70 leading-snug">
                                    <span className="not-italic font-medium">Auto-transcript:</span>{' '}
                                    {m.transcript}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          if (isVideo) {
                            return (
                              <video key={i} src={href} controls className="max-h-60 rounded border border-black/10" preload="metadata" />
                            );
                          }
                          return (
                            <a
                              key={i}
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs underline opacity-90 hover:opacity-100"
                            >
                              <Download className="w-3 h-3" />
                              {name}
                            </a>
                          );
                        })}
                      </div>
                    );
                  })()}
                </>
              )}
              {reactionEntries.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {reactionEntries.map(([emoji, uids]) => {
                    const mine = !!(agentUid && uids.includes(agentUid));
                    return (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => toggleReaction(m, emoji)}
                        className={`text-[11px] px-1.5 py-0.5 rounded-full border ${
                          mine
                            ? 'bg-primary-100 border-primary-300 text-primary-800'
                            : 'bg-white/80 border-gray-200 text-gray-700'
                        }`}
                        title={mine ? 'Remove your reaction' : 'React'}
                      >
                        <span>{emoji}</span>
                        <span className="ml-1 tabular-nums">{uids.length}</span>
                        {mine && <Check className="inline w-2.5 h-2.5 ml-0.5" />}
                      </button>
                    );
                  })}
                </div>
              )}
              {outbound && !isEditing && (
                <DeliveryReceipt
                  m={m}
                  channel={channel}
                  resending={resendingId === m.id}
                  // A system notice is not the agent's reply to re-send, and an
                  // attachment-only message has no text to push again.
                  onResend={
                    m.type === 'system' || !m.content.trim() ? undefined : () => void resendMessage(m)
                  }
                />
              )}
            </div>
          </div>
          </div>
        );
      })}
      {customerTyping && (
        <div className="flex justify-start" aria-live="polite">
          <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm flex items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wide text-gray-500 mr-1">Customer typing</span>
            <span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce" />
            <span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce" style={{ animationDelay: '120ms' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce" style={{ animationDelay: '240ms' }} />
          </div>
        </div>
      )}
      <div ref={endRef} />
      {cannedContent !== null && (
        <SaveToCannedModal
          content={cannedContent}
          onClose={(msg, ok) => {
            setCannedContent(null);
            if (msg) showToast(msg, ok ?? true);
          }}
        />
      )}
      {toast &&
        createPortal(
          <div
            role="status"
            dir="auto"
            style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1100, maxWidth: 'min(90vw, 30rem)' }}
            className={`flex items-start gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-2xl ${
              toast.kind === 'ok' ? 'bg-emerald-600' : toast.kind === 'warn' ? 'bg-amber-600' : 'bg-rose-600'
            }`}
          >
            {toast.kind === 'ok' ? (
              <Check className="w-4 h-4 shrink-0 mt-0.5" />
            ) : toast.kind === 'warn' ? (
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            ) : (
              <X className="w-4 h-4 shrink-0 mt-0.5" />
            )}
            <span className="min-w-0">{toast.msg}</span>
          </div>,
          document.body
        )}
    </div>
  );
});
